import { Card, CardContent, styled, Table as MuiTable, TableBody, TableContainer, TableHead, Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { SxProps, unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { FC, ReactNode } from 'react';
import { useResponsiveText } from '../../hooks';

interface Props {
  'data-testid'?: string;
  fixedLayout?: boolean;
  headers?: ReactNode;
  rows: ReactNode;
  pagination?: ReactNode;
  small?: boolean;
  sticky?: boolean;
  sx?: SxProps;
  title?: string;
}

const StyledCardContent = styled(CardContent)({
  padding: '0 !important',
  overflowX: 'auto',
});

interface StyledTableProps {
  tablesx: SxProps;
}

const StyledTableContainer = styled(TableContainer)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  minWidth: 'min-content',
  width: '100%',
});

const StyledTable = styled(MuiTable)<StyledTableProps>(({ tablesx }) => {
  return { ...(unstableStyleFunctionSx({ sx: tablesx } as never) as Record<string, unknown>) };
});

export const Table: FC<Props> = ({ headers, fixedLayout, rows, pagination, small, sticky, sx, title, ...props }) => {
  const theme = useTheme();

  const h5Styles = useResponsiveText('1.5rem', title?.length ?? 0, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const smallStyles = {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiTableCell-sizeSmall:first-of-type': {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
    },
    '& .MuiTableCell-sizeSmall:last-child': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(3),
    },
  };

  const styles: SxProps = {
    tableLayout: fixedLayout ? 'fixed' : 'inherit',
    ...(typeof sx === 'object' ? sx : {}),
  };

  return <Card data-testid={props['data-testid']} style={{ padding: 0 }}>
    <StyledCardContent>
      {title
        ? <Typography
          variant='h5'
          p={3}
          style={{
            fontSize: h5Styles.fontSize,
            lineHeight: h5Styles.lineHeight,
            wordBreak: h5Styles.wordBreak,
            whiteSpace: h5Styles.whiteSpace,
            overflow: h5Styles.overflow,
            textOverflow: h5Styles.textOverflow,
          }}
        >
          {title}
        </Typography>
        : <></>
      }
      <StyledTableContainer>
        <StyledTable
          stickyHeader={sticky}
          tablesx={small ? { ...styles, ...smallStyles } : styles}
          component='div'
          size={small ? 'small' : 'medium'}
        >
          <TableHead component="div">
            {headers}
          </TableHead>
          <TableBody component="div">
            {rows}
          </TableBody>
        </StyledTable>
        {pagination && <Box display='flex' justifyContent='center' marginY={1}>{pagination}</Box>}
      </StyledTableContainer>
    </StyledCardContent>
  </Card>;
};
